import React, { useRef, useEffect } from 'react';
import { Search, Barcode, Plus, Minus, ChevronDown, ChevronUp, AlertCircle, Package } from 'lucide-react';
import { Product, ProductSize } from '../../types';

export interface WizardItem {
  product: Product;
  size: ProductSize;
  quantity: number;
}

interface WizardProductSelectorProps {
  products: Product[];
  selectedItems: WizardItem[];
  onUpdateItems: (items: WizardItem[]) => void;
  stockLocation?: 'warehouseStock' | 'stock';
  themeColor?: string;
}

const WizardProductSelector: React.FC<WizardProductSelectorProps> = ({
  products,
  selectedItems,
  onUpdateItems,
  stockLocation = 'warehouseStock',
  themeColor = 'blue',
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [expandedProductId, setExpandedProductId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const expandedCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    if (!expandedProductId) return;
    const timer = setTimeout(() => {
      expandedCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 120);
    return () => clearTimeout(timer);
  }, [expandedProductId]);

  const filteredProducts = products.filter(p => {
    const q = searchQuery.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q) ||
      p.stocks.some(
        s =>
          (s.barcode || '').toLowerCase().includes(q) ||
          `${p.id}-${s.size}`.toLowerCase().includes(q),
      )
    );
  });

  /** Get committed quantity for a specific size */
  const getQty = (productId: string, sizeLabel: string) =>
    selectedItems.find(i => i.product.id === productId && i.size.size === sizeLabel)?.quantity ?? 0;

  /** Increment or decrement directly into selectedItems — no staging needed */
  const updateQty = (product: Product, size: ProductSize, delta: number) => {
    const current = getQty(product.id, size.size);
    const next = current + delta;
    if (next < 0) return;

    const alreadyCommitted = current;
    const stockAvail = size[stockLocation] ?? 0;
    if (next > stockAvail) {
      setError(`Max available for Size ${size.size} is ${stockAvail}`);
      return;
    }

    setError(null);
    const updated = selectedItems.filter(
      i => !(i.product.id === product.id && i.size.size === size.size),
    );
    if (next > 0) {
      updated.push({ product, size, quantity: next });
    }
    onUpdateItems(updated);
  };

  const handleScanForm = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim().toUpperCase();
    if (!q) return;

    let foundProduct: Product | null = null;
    let foundSize: ProductSize | null = null;

    for (const p of products) {
      const sizeMatch = p.stocks.find(s => {
        const generatedId = `${p.id}-${s.size}`.toUpperCase();
        const barcodeVal = (s.barcode || '').toUpperCase();
        return barcodeVal === q || generatedId === q;
      });
      if (sizeMatch) {
        foundProduct = p;
        foundSize = sizeMatch;
        break;
      }
    }

    if (foundProduct && foundSize) {
      setExpandedProductId(foundProduct.id);
      updateQty(foundProduct, foundSize, 1);
      setSearchQuery('');
    } else {
      setError('Article/Barcode not found.');
    }
  };

  const accentBtn = `text-${themeColor}-500 hover:text-${themeColor}-600`;

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-300">
      {error && (
        <div className="mb-3 p-2.5 bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-900/50 rounded-lg flex items-center gap-2 text-red-600 dark:text-red-400 text-[10px]">
          <AlertCircle size={14} className="shrink-0" />
          <p className="font-black uppercase tracking-tight leading-tight">{error}</p>
        </div>
      )}

      <form onSubmit={handleScanForm} className="relative mb-4 shrink-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input
          ref={inputRef}
          value={searchQuery}
          onChange={e => { setSearchQuery(e.target.value); setError(null); }}
          placeholder="Search or scan article/barcode..."
          className="w-full pl-10 pr-10 py-3 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-800 rounded-xl font-black text-xs uppercase focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all dark:text-white"
        />
        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-500">
          <Barcode size={18} />
        </button>
      </form>

      <div className="flex-1 overflow-y-auto pr-1 space-y-2 scrollbar-hide pb-4">
        {filteredProducts.map(product => {
          const isExpanded = expandedProductId === product.id;
          const totalSelected = selectedItems
            .filter(i => i.product.id === product.id)
            .reduce((s, i) => s + i.quantity, 0);

          return (
            <div
              key={product.id}
              ref={isExpanded ? expandedCardRef : null}
              className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden shadow-sm"
            >
              {/* Product header — click to expand/collapse */}
              <div
                className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                onClick={() => setExpandedProductId(isExpanded ? null : product.id)}
              >
                <img
                  src={product.image}
                  className="w-12 h-12 rounded-lg object-cover shrink-0 border border-gray-100 dark:border-gray-800"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="text-[11px] font-black dark:text-white uppercase truncate">{product.name}</h4>
                  <p className="text-[9px] font-bold text-gray-400 uppercase">{product.id}</p>
                </div>
                {totalSelected > 0 && (
                  <div className="px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-md text-[9px] font-black">
                    {totalSelected} Added
                  </div>
                )}
                <div className="text-gray-400">
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {/* Size rows — qty updates commit immediately */}
              {isExpanded && (
                <div className="p-3 bg-gray-50/50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-800 space-y-2">
                  {product.stocks.map(size => {
                    const qty = getQty(product.id, size.size);
                    const stockAvail = (size[stockLocation] ?? 0) - qty;

                    return (
                      <div
                        key={size.size}
                        className="flex items-center justify-between bg-white dark:bg-gray-800 p-2 rounded-lg border border-gray-100 dark:border-gray-700"
                      >
                        <div>
                          <span className="text-[10px] font-black dark:text-white">Size {size.size}</span>
                          <span className="text-[8px] font-bold text-gray-400 ml-2">
                            Available: {stockAvail}
                          </span>
                        </div>

                        <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-900 rounded-md border border-gray-100 dark:border-gray-700 p-0.5">
                          <button
                            type="button"
                            onClick={() => updateQty(product, size, -1)}
                            disabled={qty <= 0}
                            className={`p-1 disabled:opacity-30 text-gray-400 hover:text-red-500`}
                          >
                            <Minus size={12} />
                          </button>
                          <span className="text-[10px] font-black dark:text-white w-6 text-center">{qty}</span>
                          <button
                            type="button"
                            onClick={() => updateQty(product, size, 1)}
                            disabled={stockAvail <= 0}
                            className={`p-1 disabled:opacity-30 text-gray-400 ${accentBtn}`}
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {filteredProducts.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <Package size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-[10px] font-black uppercase tracking-widest">No articles found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WizardProductSelector;
